/**
 * Node-level federation peer authorization for the superadmin.
 *
 * Reads GET /api/federation/peers with the current user id. That endpoint is
 * superadmin-only, so a 403 doubles as the "am I the server admin" signal: we
 * expose `isServerAdmin=false` and callers hide the panel. Any other error is
 * surfaced via `error`.
 *
 *   const { peers, isServerAdmin, loading, approve, block, unblock } = useFederationPeers();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';

export function useFederationPeers() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;

    const [peers, setPeers] = useState([]);
    const [isServerAdmin, setIsServerAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // `silent` refreshes after a mutation without flipping `loading`, so the
    // table doesn't dim + spin on every click; only the first load shows it.
    const load = useCallback(({ silent = false } = {}) => {
        if (!userId) { setLoading(false); return Promise.resolve(); }
        if (!silent) setLoading(true);
        return api.get('/api/federation/peers', { params: { user_id: userId } })
            .then(data => {
                setPeers(Array.isArray(data) ? data : []);
                setIsServerAdmin(true);
                setError(null);
            })
            .catch(err => {
                if (err.status === 403) {
                    setIsServerAdmin(false);   // not the server admin — panel stays hidden
                } else {
                    setError(err);
                }
            })
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => { load(); }, [load]);

    const act = useCallback((serverId, action) => {
        const path = `/api/federation/peers/${encodeURIComponent(serverId)}/${action}`;
        return api.post(path, undefined, { params: { user_id: userId } }).then(() => load({ silent: true }));
    }, [api, userId, load]);

    return {
        peers,
        isServerAdmin,
        loading,
        error,
        refresh: load,
        approve: serverId => act(serverId, 'approve'),
        block: serverId => act(serverId, 'block'),
        unblock: serverId => act(serverId, 'unblock'),
    };
}

export default useFederationPeers;
